import { isPlatformBrowser } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpStatusCode,
} from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbilityBuilder, createMongoAbility, PureAbility } from '@casl/ability';
import { OAuthService } from 'angular-oauth2-oidc';
import {
  catchError,
  finalize,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AppAbility, PermissionDto } from './ability';
import { AuthHelper } from './auth-helper';
import { InfoResponse, UserDto } from './user.dto';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _isLoading = signal(true);
  private _hasTokens = signal(false);
  private _user = signal<UserDto | null>(null);
  private platformId = inject(PLATFORM_ID);

  readonly isLoading = computed(
    () => this._isLoading() || !isPlatformBrowser(this.platformId)
  );
  readonly isAuthenticated = computed(() => this._hasTokens());
  readonly user = computed(() => this._user());

  private oAuthService = inject(OAuthService);
  private authHelper = inject(AuthHelper);
  private router = inject(Router);
  private ability = inject(PureAbility);
  private http = inject(HttpClient);

  private _fetchUser$: Observable<UserDto | null> | null = null;

  organizacoes = signal<string[]>([]);

  constructor() {
    this.listenToOAuthEvents();
  }

  fetchUser(reload = true): Observable<UserDto | null> {
    if (this._fetchUser$) {
      return this._fetchUser$;
    }

    this.startLoading();

    this._fetchUser$ = this.authHelper
      .checkCodeExisting('code', () => !this.hasValidTokens)
      .pipe(
        switchMap(() => {
          if (!this.hasValidTokens) {
            return of(null);
          }
          if (this._user() && reload) return of(this._user());
          return forkJoin([
            this.loadUserProfile().pipe(tap((user) => this._user.set(user))),
            this.fetchAndUpdateAbility(),
            this.fetchAndUpdateOrgAbility(),
          ]).pipe(map(([a]) => a));
        }),
        finalize(() => {
          this.stopLoading();
          this._fetchUser$ = null;
        }),
        shareReplay(1)
      );

    return this._fetchUser$;
  }

  private loadUserProfile() {
    return from(
      this.oAuthService.loadUserProfile() as Promise<InfoResponse<UserDto>>
    ).pipe(map((response) => response.info));
  }

  fetchPermissions(
    format: 'id' | 'uri' = 'id',
    permission?: string
  ): Observable<PermissionDto[]> {
    if (
      !this.oAuthService.clientId ||
      !this.oAuthService.tokenEndpoint ||
      !this.oAuthService.dummyClientSecret
    ) {
      return of([]);
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'urn:ietf:params:oauth:grant-type:uma-ticket');
    body.set('audience', this.oAuthService.clientId);
    body.set('response_mode', 'permissions');
    body.set('permission_resource_format', format);
    if (permission) {
      body.set('permission', permission);
    }
    return this.http
      .post<PermissionDto[]>(this.oAuthService.tokenEndpoint, body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${this.getAccessToken()}`,
        }),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          if (error.status == HttpStatusCode.Unauthorized) {
            this.logout();
          }
          return of([]);
        })
      );
  }

  private updateAbility(permissions: PermissionDto[]) {
    const { can, rules } = new AbilityBuilder<AppAbility>(createMongoAbility);
    permissions.forEach((permission) => {
      if (permission.scopes) {
        permission.scopes?.forEach((scope) => {
          can(scope, permission.rsname);
        });
      }
    });
    this.ability.update(rules);
  }
  fetchAndUpdateOrgAbility(): Observable<PermissionDto[]> {
    return this.fetchPermissions('uri', '/organizacao').pipe(
      tap((permissions) =>
        permissions.forEach((permission) => {
          this.organizacoes.update((items) => [...items, permission.rsname]);
        })
      )
    );
  }

  fetchAndUpdateAbility(): Observable<PermissionDto[]> {
    return this.fetchPermissions().pipe(
      tap((permissions) => this.updateAbility(permissions))
    );
  }

  private listenToOAuthEvents(): void {
    const relevantEvents = [
      'token_received',
      'token_refreshed',
      'session_changed',
      'discovery_document_loaded',
    ];

    this.oAuthService.events.subscribe((e) => {
      if (relevantEvents.includes(e.type)) {
        this.fetchUser(true)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.updateAuthState();
            },
            error: (err) => {
              console.error('Error fetching user:', err);
            },
          });
      }
      if (e.type === 'user_profile_load_error') {
        console.error('User profile loading failed:', e);
        this.logout();
      }
    });
  }

  updateAuthState(): void {
    const hasValid = this.hasValidTokens;
    this._hasTokens.set(hasValid);
    //if (hasValid) this.updateUser();
  }

  private updateUser(): void {
    const claims = this.oAuthService.getIdentityClaims();
    this._user.set((claims as UserDto) ?? null);
  }

  getAccessToken() {
    return this.oAuthService.getAccessToken();
  }

  account() {
    const accountUrl = new URL(this.oAuthService.issuer + '/account');
    accountUrl.searchParams.set('referrer', environment.appName);
    accountUrl.searchParams.set('referrer_uri', document.location.href);
    document.location.href = accountUrl.toString();
  }

  login(path?: string): void {
    if (!this.isAuthenticated())
      this.oAuthService.initCodeFlow(path ?? this.router.url);
  }

  logout(noRedirectToLogoutUrl = false): void {
    this.oAuthService.logOut(noRedirectToLogoutUrl);
    this._user.set(null);
    this._hasTokens.set(false);
    this.ability.update([]);
    this.stopLoading();
  }

  startLoading(): void {
    this._isLoading.set(true);
  }

  stopLoading(): void {
    if (isPlatformBrowser(this.platformId)) {
      this._isLoading.set(false);
    }
  }

  get hasValidTokens(): boolean {
    return (
      this.oAuthService.hasValidAccessToken() &&
      this.oAuthService.hasValidIdToken()
    );
  }
}
